// The signed-in navigation, as data.
//
// Before this file the same twelve links were written out longhand in App.tsx,
// which meant the sidebar could only ever exist in one place and one shape:
// adding a rail, a collapsed state or a mobile drawer meant writing the list
// three more times. It is a list now, rendered by whatever needs it.
//
// EVERY ITEM CARRIES A `title`, AND THE RULE IS NOT DECORATION: an item that
// leads to a screen touching a connected Facebook, Instagram or YouTube account
// says what data is involved there, in plain words, before it is pressed.
// Meta's Platform Terms ask that people be told what an app does with their
// data at the point they act, and "it was on the privacy policy" is not the
// same as telling someone at the door. The rest say what the screen is for.
// A new entry without one is a gap in that, not a missing nicety.

import type { NavIcon } from "./icons";
import {
  BillingIcon, CalendarIcon, CaptionsIcon, ClientsIcon, ComposeIcon, ConnectionsIcon,
  DashboardIcon, MediaIcon, OrganisationIcon, PeopleIcon, PostsIcon, PricingIcon,
  ProfileIcon, StudioIcon, SupportIcon,
} from "./icons";
import { DASHBOARD_ROUTE } from "../dashboard";
import { STUDIO_ROUTE } from "../studio";
import { BILLING_PERMISSION, BILLING_ROUTE } from "../billing";

/** What the sidebar knows about the reader when it decides what to show. */
export interface NavContext {
  /** The active workspace's permission check, from `useWorkspace()`. */
  can: (permission: string) => boolean;
  /** "agency" workspaces have clients; nobody else does. */
  kind: string | null;
}

export interface NavItem {
  id: string;
  label: string;
  route: string;
  icon: NavIcon;
  /** A workspace permission needed to see this item at all. */
  permission?: string;
  /** Anything the permission cannot express. */
  when?: (ctx: NavContext) => boolean;
  /** The tooltip and the accessible description. Required — see the note above. */
  title: string;
}

export interface NavGroup {
  id: string;
  /** Shown above the group when the sidebar is expanded; read by the group's
   *  `aria-label` in either state. */
  label: string;
  items: NavItem[];
}

/**
 * The primary navigation.
 *
 * Order is meaning: the first group is the work, the second is who and what it
 * is done with, the third is what it costs. A new screen belongs in one of the
 * three, not at the end.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "work",
    label: "Workspace",
    items: [
      {
        // First, because it is the answer to "how is it going" and everything
        // else on this bar is a place to do work.
        id: "dashboard",
        label: "Dashboard",
        route: DASHBOARD_ROUTE,
        icon: DashboardIcon,
        permission: "post.view",
        title:
          "What this workspace published, where it went, and which connected accounts still " +
          "work. Delivery records we hold — no likes, views, reach or follower counts, which " +
          "we never ask Facebook, Instagram or YouTube for.",
      },
      {
        id: "compose",
        label: "Compose",
        route: "/compose",
        icon: ComposeIcon,
        title:
          "Write a post and choose which connected accounts it goes to. Nothing is published " +
          "to a Facebook Page, Instagram account or YouTube channel unless you tick it here.",
      },
      {
        // Beside Compose, because that is where what it produces is used.
        // Gated on the role only: the plan gate lives on the screen, where it
        // can say what the plan is missing and link to the fix. A nav item
        // that vanishes teaches nobody that the feature exists.
        id: "studio",
        label: "Studio",
        route: STUDIO_ROUTE,
        icon: StudioIcon,
        permission: "content.ai_generate",
        title:
          "Draft captions and images with AI. It works from what you type — it is never given " +
          "your connected accounts or anything read from them.",
      },
      {
        // Beside Posts, because they are the same rows asked a different
        // question: Posts is "what have I made", the calendar is "what goes
        // out, and when". Gated the same way — seeing either is `post.view`.
        id: "calendar",
        label: "Calendar",
        route: "/calendar",
        icon: CalendarIcon,
        permission: "post.view",
        title:
          "The same posts as Posts, asked when rather than what. Your own schedule — nothing " +
          "here is read back from a network.",
      },
      {
        id: "posts",
        label: "Posts",
        route: "/posts",
        icon: PostsIcon,
        title:
          "Everything you have composed, and what happened to each destination — published, " +
          "failed, or still going. Links out to the live post once a network confirms it.",
      },
      {
        id: "media",
        label: "Media",
        route: "/media",
        icon: MediaIcon,
        title:
          "Photos and videos you have uploaded, stored privately until a post sends one to a " +
          "network you chose.",
      },
      {
        // Beside Media, because it is the same idea about a different thing:
        // one library of pictures, one of words, both filled away from the
        // screen that publishes.
        id: "captions",
        label: "Captions",
        route: "/captions",
        icon: CaptionsIcon,
        title: "Saved caption text, reusable across posts. Yours, kept here, never read from a network.",
      },
    ],
  },
  {
    id: "manage",
    label: "Accounts & people",
    items: [
      {
        // The one item whose tooltip is a disclosure rather than a hint: it is
        // the door to granting Facebook, Instagram and YouTube access, and the
        // screen behind it says the same thing at more length.
        id: "connections",
        label: "Connections",
        route: "/connections",
        icon: ConnectionsIcon,
        title:
          "Connect or disconnect a Facebook Page, Instagram professional account or YouTube " +
          "channel. Shows exactly which permissions each account granted, and lets you revoke " +
          "ours at any time.",
      },
      {
        id: "people",
        label: "People",
        route: "/people",
        icon: PeopleIcon,
        permission: "member.view",
        title:
          "Who is in this workspace and what each of them may do. Nobody publishing on your " +
          "behalf ever receives a connected account's credentials.",
      },
      {
        // Not gated on a workspace permission: `member.view` says what you may
        // see in the *active workspace*, and everybody has a personal
        // organisation of their own regardless (ADR-0022). Hiding this behind a
        // workspace role would hide somebody's own roster from them.
        id: "organisation",
        label: "Organisation",
        route: "/organisation",
        icon: OrganisationIcon,
        title: "The businesses you belong to, and the workspaces inside each.",
      },
      {
        // Only an agency has clients. Shown rather than always-present, because
        // a link to an empty concept is worse than no link.
        id: "clients",
        label: "Clients",
        route: "/agency",
        icon: ClientsIcon,
        permission: "member.view",
        when: ({ kind }) => kind === "agency",
        title:
          "Clients who have lent this agency an account to publish to, and what each lend " +
          "allows. A lend can be withdrawn by the client at any time.",
      },
    ],
  },
  {
    id: "plan",
    label: "Plan",
    items: [
      {
        id: "pricing",
        label: "Pricing",
        route: "/plans",
        icon: PricingIcon,
        title: "Plans, what each includes, and how many accounts each allows.",
      },
      {
        // Beside Pricing, because they are the two halves of one question:
        // Pricing is "what could we be on", this is "what are we on and what
        // have we spent". Gated, unlike everything above it — consumption and
        // the plan are the workspace's finances, and a creator has no business
        // reading a breakdown of their colleagues' usage. The nav link is a
        // courtesy; `workspace_usage()` is the gate.
        id: "billing",
        label: "Billing",
        route: BILLING_ROUTE,
        icon: BillingIcon,
        permission: BILLING_PERMISSION,
        title: "What this workspace is on and what it has used this period.",
      },
    ],
  },
];

/**
 * The two rows pinned to the bottom of the sidebar.
 *
 * Separated from `NAV_GROUPS` because they are not the work: they are the
 * account and the way out of trouble, and they stay reachable at the same
 * place on every screen.
 */
export const NAV_FOOTER: NavItem[] = [
  {
    id: "profile",
    label: "Your account",
    route: "/profile",
    icon: ProfileIcon,
    title: "Your sign-in, your email address, and deleting your account.",
  },
  {
    id: "support",
    label: "Help & support",
    route: "/support",
    icon: SupportIcon,
    title: "How to reach a person, and answers to the questions asked most often.",
  },
];

/** The items of one group this reader may actually see. */
export const visibleItems = (group: NavGroup, ctx: NavContext): NavItem[] =>
  group.items.filter((i) => (!i.permission || ctx.can(i.permission)) && (!i.when || i.when(ctx)));

/** Every group with something in it, so an empty heading is never drawn. */
export const visibleGroups = (ctx: NavContext): Array<NavGroup & { items: NavItem[] }> =>
  NAV_GROUPS.map((g) => ({ ...g, items: visibleItems(g, ctx) })).filter((g) => g.items.length > 0);
